import {Suspense, useEffect, useState} from 'react'
import type {ReactElement} from 'react'
import {createPortal} from 'react-dom'
import {useLocation} from 'react-router-dom'
import {Icon} from 'pepsico-ds'
import {usePageDocs} from '@/hooks/usePageDocs'
import {lazyLoad} from '@/utils/helperMethods'

const loadPageDocsSidePanel = () => import('./PageDocsSidePanel')
const prefetchPanel = (): void => {
  loadPageDocsSidePanel().catch(() => undefined)
}
// The slider (markdown renderer, CMS service, video player) is only fetched once a user asks for the docs.
const PageDocsSidePanel = lazyLoad(loadPageDocsSidePanel)

/**
 * Floating "docs" file icon (bottom right) that opens the docs in a right-side slider.
 * While the slider is open the icon is hidden and the slider's own header close button closes it.
 * Configured by `usePageDocs`: `enabled` controls visibility and `slug` decides which DatoCMS section or
 * article is rendered in the slider. The slider chunk is prefetched when the icon is hovered or focused.
 */
const PageDocsButton = (): ReactElement | null => {
  const {pathname} = useLocation()
  const {enabled: docsEnabled, slug: docsSlug} = usePageDocs()
  const [isOpen, setIsOpen] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)

  useEffect(() => {
    setIsOpen(false)
  }, [pathname])

  if (!docsEnabled) return null

  const open = (): void => {
    setHasOpened(true)
    setIsOpen(true)
  }

  return (
    <>
      {!isOpen &&
        createPortal(
          <button
            type="button"
            title="Documentation"
            aria-label="Open documentation for this page"
            data-testid="page-docs-button"
            onClick={open}
            onMouseEnter={prefetchPanel}
            onFocus={prefetchPanel}
            // z-150 keeps it above page content but below the fixed header (z-200) and its profile menu
            className="fixed right-6 bottom-6 z-[150] flex h-14 w-14 cursor-pointer items-center justify-center rounded-full border-none bg-[var(--color-background-brand,#004b93)] text-white shadow-lg transition-[box-shadow,filter] duration-200 hover:shadow-[0_10px_28px_rgba(0,75,147,0.45)] hover:brightness-110 focus-visible:shadow-[0_10px_28px_rgba(0,75,147,0.45)]"
          >
            <Icon icon="description" size="large" style={{color: 'currentColor'}} />
          </button>,
          document.body,
        )}
      {hasOpened && (
        <Suspense fallback={null}>
          <PageDocsSidePanel isOpen={isOpen} onClose={(): void => setIsOpen(false)} docsSlug={docsSlug} />
        </Suspense>
      )}
    </>
  )
}

export default PageDocsButton
